import React, { useCallback, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { reportsAPI } from '../../services/api';
import useReport from './shared/useReport';
import KpiStrip from './shared/KpiStrip';
import ChartPanel from './shared/ChartPanel';
import ReportTable from './shared/ReportTable';
import ReportPrintLayout from './shared/ReportPrintLayout';
import PrintButton from './shared/PrintButton';
import DateRangeFilter, { getPresetRange } from './shared/DateRangeFilter';
import { PALETTE, axisProps, fmtNum, tooltipStyle, barColor } from './shared/chartTheme';
import useDrill from './shared/useDrill';

// How busy the OPD is: visit volume over time, the weekly rhythm, and what share of visits are
// emergencies, revisits, or got cancelled.

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const COLUMNS = [
  { key: 'bucket', label: 'Period' },
  { key: 'visits', label: 'Visits', align: 'right' },
  { key: 'uniquePatients', label: 'Unique Patients', align: 'right' },
  { key: 'completed', label: 'Completed', align: 'right' },
  { key: 'emergencies', label: 'Emergencies', align: 'right' },
  { key: 'revisits', label: 'Revisits', align: 'right' },
  { key: 'cancelled', label: 'Cancelled', align: 'right' },
];

const VisitTrendsReport = () => {
  const drill = useDrill();
  const [range, setRange] = useState(() => getPresetRange('last30'));
  const [preset, setPreset] = useState('last30');
  const [granularity, setGranularity] = useState('daily');

  const fetcher = useCallback(
    () => reportsAPI.getVisitTrends({ from: range.from, to: range.to, granularity }),
    [range, granularity]
  );
  const { data, loading } = useReport(fetcher);

  const k = data?.kpis || {};
  const kpis = [
    { label: 'Visits', value: fmtNum(k.visits ?? 0) },
    { label: granularity === 'monthly' ? 'Avg / Month' : 'Avg / Day', value: fmtNum(k.avgPerBucket ?? 0) },
    { label: 'Busiest', value: k.peakBucket || '—', sub: k.peakVisits ? `${k.peakVisits} visits` : undefined },
    { label: 'Emergencies', value: fmtNum(k.emergencies ?? 0) },
    { label: 'Revisits', value: fmtNum(k.revisits ?? 0), sub: 'same-day returns' },
    { label: 'Cancelled', value: fmtNum(k.cancelled ?? 0), tone: Number(k.cancelled) > 0 ? 'alert' : undefined },
  ];

  const series = data?.series || [];
  // A trend needs more than one day. With Today (or any single day) the line had one point and so
  // drew nothing, and the weekday chart one bar repeating the day's count (owner, 2026-10-22).
  const oneDay = range.from === range.to;
  // A dot on every day makes a short period readable; past two months of days they run together.
  const lineDot = series.length <= 62 ? { r: 3, fill: PALETTE[0], strokeWidth: 0 } : false;

  const weekdays = useMemo(
    () =>
      (data?.weekdays || []).map((w) => ({
        day: WEEKDAYS[(Number(w.dow) || 1) - 1],
        avg: w.daysOccurred > 0 ? Math.round((Number(w.visits) / Number(w.daysOccurred)) * 10) / 10 : 0,
      })),
    [data]
  );

  return (
    <div className="space-y-5">
      <DateRangeFilter value={range} preset={preset} onChange={(r, p) => { setRange(r); setPreset(p); }} />

      <div className="flex items-center gap-3">
        <div className="bg-white rounded-xl shadow p-1 inline-flex">
          {['daily', 'monthly'].map((g) => (
            <button
              key={g}
              onClick={() => setGranularity(g)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize ${
                granularity === g ? 'bg-navy-800 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {g}
            </button>
          ))}
        </div>
        <div className="ml-auto"><PrintButton /></div>
      </div>

      <KpiStrip items={kpis} loading={loading} />

      {oneDay ? (
        <div className="bg-white rounded-2xl shadow p-5">
          <p className="text-gray-500 text-sm text-center py-6">
            This period is a single day, so there is no trend to draw. Pick Last 7 Days or longer to see
            visits over time and which weekdays are busiest.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <ChartPanel title="Visits over time" className="lg:col-span-2">
            {series.length === 0 ? (
              <p className="text-gray-400 text-sm text-center py-16">No visits in the selected range</p>
            ) : series.length === 1 ? (
              <p className="text-gray-500 text-sm text-center py-16">
                {granularity === 'monthly'
                  ? 'This period is a single month, so there is no line to draw. Choose Daily, or a longer period.'
                  : 'Visits were made on only one day in this period, so there is no line to draw.'}
              </p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={series} margin={{ top: 8, left: 8, right: 16 }}>
                  <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" vertical={false} />
                  <XAxis dataKey="bucket" {...axisProps} />
                  <YAxis {...axisProps} allowDecimals={false} />
                  <Tooltip {...tooltipStyle} />
                  <Line type="monotone" dataKey="visits" name="Visits" stroke={PALETTE[0]} strokeWidth={2} dot={lineDot} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </ChartPanel>
          <ChartPanel title="Average visits by weekday">
            {!weekdays.some((w) => w.avg > 0) ? (
              <p className="text-gray-400 text-sm text-center py-16">No visits in the selected range</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={weekdays} margin={{ left: 8, right: 8 }}>
                  <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" vertical={false} />
                  <XAxis dataKey="day" {...axisProps} />
                  <YAxis {...axisProps} allowDecimals={false} />
                  <Tooltip {...tooltipStyle} />
                  <Bar dataKey="avg" name="Avg visits" radius={[4, 4, 0, 0]} barSize={22}>
                    {weekdays.map((_, i) => <Cell key={i} fill={barColor(i)} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartPanel>
        </div>
      )}

      <ReportTable
        onRowClick={(r) => drill.open({ type: 'period', value: r.bucket, granularity, from: range.from, to: range.to })}
        title="Visits by period"
        columns={COLUMNS}
        rows={series}
        csvName={`visit-trends-${range.from}-to-${range.to}`}
      />

      <ReportPrintLayout
        title="Visit Trends"
        subtitle={`Period: ${range.from} to ${range.to} (${granularity})`}
        kpis={kpis.map(({ label, value }) => ({ label, value }))}
        tables={[{ title: 'Visits by period', columns: COLUMNS, rows: data?.series || [] }]}
      />

      {drill.node}
    </div>
  );
};

export default VisitTrendsReport;
